import type {
  AdminPaginatedEnvelope,
  BusinessPoolAccount,
  BusinessPoolAccountFlags,
} from '@/types';
import type {AfriexQueryParams} from '@/types/AfriexQueryParams';

export type PoolAccountListProps<T> = {
  // Whatever the fetch threw, held only so a consumer could react to it
  // (none does today). `typeof Error` said "the Error constructor" rather
  // than "an Error", and the caught value is not required to be either.
  error: unknown;
  isLoading: boolean;
  isSearch?: boolean;
  isPageReset?: boolean;
  pageInfo: {
    total: number;
    page: number;
    summary?: AdminPaginatedEnvelope<T[]>['summary'];
  };
  list: T[];
  fetchList: (params: AfriexQueryParams) => void;
  setPage: (page: number) => void;
  filterCount: number;
  currentFilters: AfriexQueryParams;
  filtersToApply: AfriexQueryParams;
  clearFilters: () => void;
  selectAppliedFilters: (
    name: string,
    value: string | Date | number | undefined,
  ) => void;
  handleApplyFilters: () => void;
  selectCurrentFilters: (
    name: string,
    value: string | Date | undefined,
  ) => void;
  handleAutoComplete: (t: string) => void;
};

export enum PoolAccountActionType {
  UPDATE_POOL_ACCOUNT = 'UPDATE_POOL_ACCOUNT',
  UPDATE_POOL_ACCOUNT_FLAG = 'UPDATE_POOL_ACCOUNT_FLAG',
  UPDATE_POOL_ACCOUNT_EXTRA_PROPS = 'UPDATE_POOL_ACCOUNT_EXTRA_PROPS',
  ADD_POOL_ACCOUNT_EXTRA_PROP = 'ADD_POOL_ACCOUNT_EXTRA_PROP',
  REMOVE_POOL_ACCOUNT_EXTRA_PROP = 'REMOVE_POOL_ACCOUNT_EXTRA_PROP',
}

interface UpdatePoolAccountAction {
  type: PoolAccountActionType.UPDATE_POOL_ACCOUNT;

  payload: Partial<BusinessPoolAccount>;
}

interface UpdatePoolAccountFlagAction {
  type: PoolAccountActionType.UPDATE_POOL_ACCOUNT_FLAG;

  payload: Partial<BusinessPoolAccountFlags>;
}

interface UpdatePoolAccountExtraPropsAction {
  type: PoolAccountActionType.UPDATE_POOL_ACCOUNT_EXTRA_PROPS;
  payload: Record<string, unknown>;
}

interface AddPoolAccountExtraPropAction {
  type: PoolAccountActionType.ADD_POOL_ACCOUNT_EXTRA_PROP;
  payload: {key: string; value: unknown};
}

interface RemovePoolAccountExtraPropAction {
  type: PoolAccountActionType.REMOVE_POOL_ACCOUNT_EXTRA_PROP;
  payload: string;
}

export type PoolAccountAction =
  | UpdatePoolAccountAction
  | UpdatePoolAccountFlagAction
  | UpdatePoolAccountExtraPropsAction
  | AddPoolAccountExtraPropAction
  | RemovePoolAccountExtraPropAction;
